import type { Bundle } from "@medplum/fhirtypes";
import type { PaginationMetadata } from "../types/pagination-metadata";

export const OSS_BUNDLE_PAGE_SIZE = 100;

export type BundlesPageResponse = {
  metadata: PaginationMetadata;
  results: Bundle[];
};

/**
 * Eagerly fetches all pages of a paginated bundle endpoint into a single in-memory array.
 * The OSS API caps each response at OSS_BUNDLE_PAGE_SIZE items, so multiple requests
 * may be needed for patients with many results.
 */
export async function fetchAllBundlePages(
  fetchPage: (limit: number, offset: number) => Promise<BundlesPageResponse>
): Promise<{ results: Bundle[]; totalFromApi: number }> {
  const all: Bundle[] = [];
  let offset = 0;
  let totalFromApi = 0;

  const firstPage = await fetchPage(OSS_BUNDLE_PAGE_SIZE, offset);
  totalFromApi = firstPage.metadata.total;
  all.push(...firstPage.results);

  if (firstPage.results.length === 0 || all.length >= totalFromApi) {
    return { results: all, totalFromApi };
  }

  offset += OSS_BUNDLE_PAGE_SIZE;

  while (offset < totalFromApi) {
    const page = await fetchPage(OSS_BUNDLE_PAGE_SIZE, offset);
    all.push(...page.results);
    if (page.results.length === 0) {
      break;
    }
    offset += OSS_BUNDLE_PAGE_SIZE;
  }

  return { results: all, totalFromApi };
}
